import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Plus, Save, Trash2, Image as ImageIcon } from "lucide-react";
import { adminApi, api as publicApi } from "../../lib/api";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { useToast } from "../../hooks/use-toast";

const emptyMember = { habbo_name: "", position: "", since: new Date().toISOString().slice(0,10), bio: "", order: 99, background_image_url: "" };

const TeamPanel = ({ token }) => {
  const a = useMemo(() => adminApi(token), [token]);
  const { toast } = useToast();
  const [members, setMembers] = useState([]);
  const [backgrounds, setBackgrounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyMember);
  const [creating, setCreating] = useState(false);
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [t, allShop] = await Promise.all([a.listTeam(), publicApi.listShop()]);
      setMembers(t);
      setBackgrounds((allShop || []).filter((i) => i.type === "background"));
    } finally { setLoading(false); }
  };
  useEffect(() => { load();  }, []);

  const onCreate = async () => {
    if (!form.habbo_name.trim() || !form.position.trim()) return;
    setCreating(true);
    try {
      await a.createTeam(form);
      setForm(emptyMember);
      toast({ title: "Teammitglied angelegt" });
      await load();
    } catch (err) {
      toast({ title: "Fehler", description: err?.response?.data?.detail, variant: "destructive" });
    } finally { setCreating(false); }
  };

  const onSave = async (m) => {
    setSavingId(m.id);
    try {
      await a.updateTeam(m.id, {
        habbo_name: m.habbo_name, position: m.position, since: m.since,
        bio: m.bio || "", order: m.order || 0,
        background_image_url: m.background_image_url || null,
      });
      toast({ title: "Gespeichert" });
      await load();
    } catch (err) {
      toast({ title: "Fehler", description: err?.response?.data?.detail, variant: "destructive" });
    } finally { setSavingId(null); }
  };

  const onDelete = async (m) => {
    if (!window.confirm(`"${m.habbo_name}" entfernen?`)) return;
    try { await a.deleteTeam(m.id); toast({ title: "Entfernt" }); await load(); }
    catch { toast({ title: "Fehler", variant: "destructive" }); }
  };

  const update = (id, field, val) =>
    setMembers((prev) => prev.map((x) => (x.id === id ? { ...x, [field]: val } : x)));

  const avatarUrl = (name) =>
    `https://www.habbo.de/habbo-imaging/avatarimage?user=${encodeURIComponent(name)}&direction=2&head_direction=3&size=l&action=std&gesture=sml`;

  return (
    <div>
      <div className="rounded-xl border border-white/10 bg-[#0f141b] p-5">
        <div className="text-sm font-semibold text-white">Neues Teammitglied</div>
        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-[140px_1.5fr_1fr_1fr_auto]">
          <div className="flex items-center justify-center rounded-md border border-white/10 bg-[#10141b] p-2">
            {form.habbo_name.trim() ? (
              <img src={avatarUrl(form.habbo_name.trim())} alt="" className="hb-pixel-img h-20" />
            ) : (
              <span className="text-xs text-zinc-600">Vorschau</span>
            )}
          </div>
          <Input value={form.habbo_name} onChange={(e) => setForm({ ...form, habbo_name: e.target.value })} placeholder="Habbo-Name (exakt)" className="bg-[#10141b] border-white/10 text-zinc-200" />
          <Input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} placeholder="Position (z.B. Chefredakteur)" className="bg-[#10141b] border-white/10 text-zinc-200" />
          <Input type="date" value={form.since} onChange={(e) => setForm({ ...form, since: e.target.value })} className="bg-[#10141b] border-white/10 text-zinc-200" />
          <Button onClick={onCreate} disabled={creating || !form.habbo_name.trim() || !form.position.trim()} className="btn-primary rounded-lg px-4 text-sm">
            {creating ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Plus className="mr-1 h-4 w-4" />} Anlegen
          </Button>
          <Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} placeholder="Kurzer Info-Text (optional)" rows={2} className="md:col-span-5 bg-[#10141b] border-white/10 text-zinc-200" />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : (
        <div className="mt-6 space-y-3">
          {members.map((m) => (
            <div key={m.id} className="flex flex-col gap-3 rounded-xl border border-white/10 bg-[#0f141b] p-4 md:flex-row md:items-center">
              <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-md border border-white/10 bg-[#10141b]">
                <img src={avatarUrl(m.habbo_name)} alt={m.habbo_name} className="hb-pixel-img h-20" loading="lazy" />
              </div>
              <div className="grid flex-1 grid-cols-1 gap-2 md:grid-cols-4">
                <Input value={m.habbo_name} onChange={(e) => update(m.id, "habbo_name", e.target.value)} placeholder="Habbo-Name" className="bg-[#10141b] border-white/10 text-zinc-200" />
                <Input value={m.position} onChange={(e) => update(m.id, "position", e.target.value)} placeholder="Position" className="bg-[#10141b] border-white/10 text-zinc-200" />
                <Input type="date" value={m.since} onChange={(e) => update(m.id, "since", e.target.value)} className="bg-[#10141b] border-white/10 text-zinc-200" />
                <Input type="number" value={m.order || 0} onChange={(e) => update(m.id, "order", parseInt(e.target.value || "0", 10))} placeholder="Order" className="bg-[#10141b] border-white/10 text-zinc-200" />
                <Textarea value={m.bio || ""} onChange={(e) => update(m.id, "bio", e.target.value)} placeholder="Bio" rows={2} className="md:col-span-4 bg-[#10141b] border-white/10 text-zinc-200" />
                <div className="md:col-span-4">
                  <label className="mb-1 flex items-center gap-1 text-[10px] uppercase tracking-wider text-zinc-500">
                    <ImageIcon className="h-3 w-3" /> Hintergrund zuweisen (Admin-Override, optional)
                  </label>
                  <div className="flex gap-2">
                    <select
                      value={m.background_image_url || ""}
                      onChange={(e) => update(m.id, "background_image_url", e.target.value)}
                      data-testid={`team-bg-select-${m.id}`}
                      className="flex-1 rounded-md border border-white/10 bg-[#10141b] px-2 py-1.5 text-xs text-zinc-200"
                    >
                      <option value="">— Kein Override (nutzt User-Active-Background, falls vorhanden) —</option>
                      {backgrounds.map((b) => (
                        <option key={b.id} value={b.image_url}>{b.name} · {b.cost}p</option>
                      ))}
                    </select>
                    <Input
                      value={m.background_image_url || ""}
                      onChange={(e) => update(m.id, "background_image_url", e.target.value)}
                      placeholder="oder eigene Bild-URL"
                      className="flex-1 bg-[#10141b] border-white/10 text-zinc-200 text-xs"
                      data-testid={`team-bg-url-${m.id}`}
                    />
                  </div>
                  {m.background_image_url && (
                    <div className="mt-2 h-12 w-full overflow-hidden rounded border border-white/10">
                      <img src={m.background_image_url} alt="bg" className="h-full w-full object-cover" />
                    </div>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => onSave(m)} disabled={savingId === m.id} className="btn-primary rounded-lg px-3 py-2 text-sm">
                  {savingId === m.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                </Button>
                <Button onClick={() => onDelete(m)} className="btn-ghost rounded-lg px-3 py-2 text-sm text-rose-300 hover:border-rose-500/40">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
          {members.length === 0 && (
            <div className="py-10 text-center text-sm text-zinc-500">Noch keine Teammitglieder.</div>
          )}
        </div>
      )}
    </div>
  );
};

export default TeamPanel;
